import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { css, StyleSheet } from 'aphrodite';
import type { ReactNode, SyntheticEvent } from 'react';
import { useCallback } from 'react';
import { Link, NavLink } from 'react-router-dom';

import {
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  transition,
} from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import {
  useAccessibilityMode,
  accessibilityModeStyle,
  FOCUSED_ACCESSIBILITY_MODE_BORDER_WIDTH,
} from 'ms-utils/accessibility';
import { tappable } from 'ms-utils/emotion';
import { useRouter } from 'ms-utils/hooks/useRouter';
import keyDownMap from 'ms-utils/keyDownMap';

// It is convenient to make sure that these two matches
const BORDER_HEIGHT = FOCUSED_ACCESSIBILITY_MODE_BORDER_WIDTH;
const ACTIVE_BORDER_HEIGHT = BORDER_HEIGHT * 2;
const ACTIVE_BORDER_HEIGHT_V2 = BORDER_HEIGHT * 2 - 1;
const GUTTER = 8 * BASE_UNIT;

const TAB_HEIGHT = 58;
export const TAB_HEIGHT_V2 = 46;
export const BaseTabs = styled('div', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'color',
})<{ v2?: boolean }>(({ v2 }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  fontFamily: fontFamily.body,
  // important not to set `borderColor` here so that
  // it can be overridden by custom implementations of `Tabs`
  borderBottomWidth: BORDER_HEIGHT,
  borderBottomStyle: 'solid',
  height: v2 ? TAB_HEIGHT_V2 : TAB_HEIGHT,
}));

type TabsProps = { borderColor?: string; borderWidth?: number };
export const Tabs = styled(BaseTabs)<TabsProps>(
  ({ borderColor = colors.ironLight, borderWidth }) => ({
    color: colors.grey90,
    borderBottomColor: borderColor,
    borderWidth,
  }),
);

export const TabsAside = styled.div({
  marginLeft: 'auto',

  '&:nth-of-type(1n) > *': {
    marginLeft: GUTTER,
    marginRight: 0,
  },
});

type TabWrapperProps = {
  active?: boolean | undefined;
  accessibilityMode?: boolean | undefined;
  evenlyFillSpace?: boolean | undefined;
  v2?: boolean | undefined;
};

const tabWrapperStyle = {
  default: {
    ...tappable,
    position: 'relative',
    display: 'inline-block',
    textDecoration: 'none',
    color: 'inherit',
    paddingLeft: GUTTER / 2,
    paddingRight: GUTTER / 2,
    borderBottom: `${ACTIVE_BORDER_HEIGHT}px solid transparent`,
    height: TAB_HEIGHT,
  } as const,
  active: {
    color: colors.cloudBurst,
    borderBottom: `${ACTIVE_BORDER_HEIGHT}px solid ${colors.grey}`,
  },
  v2: {
    borderBottom: `${ACTIVE_BORDER_HEIGHT_V2}px solid transparent`,
    paddingLeft: 12,
    paddingRight: 12,
    height: TAB_HEIGHT_V2,
  },
  activeV2: {
    color: colors.eggplant,
    borderBottom: `${ACTIVE_BORDER_HEIGHT_V2}px solid ${colors.eggplant}`,
  },
  accessibilityMode: accessibilityModeStyle,
  evenlyFillSpace: {
    flex: 1,
  },
};

const getTabWrapperStyles = ({
  active,
  accessibilityMode,
  evenlyFillSpace,
  v2,
}: TabWrapperProps) => ({
  ...tabWrapperStyle.default,
  ...(active && tabWrapperStyle.active),
  ...(accessibilityMode && tabWrapperStyle.accessibilityMode),
  ...(evenlyFillSpace && tabWrapperStyle.evenlyFillSpace),
  ...(v2 && tabWrapperStyle.v2),
  ...(active && v2 && tabWrapperStyle.activeV2),
});

const tabWrapperProps = [
  'active',
  'accessibilityMode',
  'evenlyFillSpace',
  'v2',
];
const removeWrapperPropsOptions = {
  shouldForwardProp: (prop: string) => !tabWrapperProps.includes(prop),
};

// I tried to make these a little neater via a helper function but I couldn't
// get the typing correct, so I left it as is. Better to have strong type safety
// than to have a few less lines here.
const AnchorTabWrapper = styled(
  'a',
  removeWrapperPropsOptions,
)<TabWrapperProps>(getTabWrapperStyles);
const LinkTabWrapper = styled(
  Link,
  removeWrapperPropsOptions,
)<TabWrapperProps>(getTabWrapperStyles);
const TabWrapper = styled(
  'div',
  removeWrapperPropsOptions,
)<TabWrapperProps>(getTabWrapperStyles);
const navLinkTabStyles = StyleSheet.create(tabWrapperStyle);

export const InnerTab = styled('div', {
  shouldForwardProp: prop => prop !== 'v2',
})<{ v2?: boolean }>(({ v2 = false }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: `all ${transition}`,
  fontWeight: v2 ? fontWeight.semibold : fontWeight.normal,

  // this is to persist the custom alignment after the removal of transform from TabWrapper
  transform: v2 ? 'none' : 'translateY(1px)',
}));

export const TAB_CONTENT_PADDING_VERTICAL = 4 * BASE_UNIT;
export const TAB_CONTENT_PADDING_VERTICAL_V2 = 3 * BASE_UNIT;

// This is the recommended tab content wrapper
// Some consumers of this component may choose to re-implement this
// for custom tab style
export const BaseTabContent = styled.div({
  paddingTop: TAB_CONTENT_PADDING_VERTICAL,
  paddingBottom: TAB_CONTENT_PADDING_VERTICAL,
  fontSize: fontSize.medium,
  lineHeight: lineHeight.title,
  whiteSpace: 'nowrap',
});

export const TabContent = styled('div', {
  shouldForwardProp: prop => prop !== 'v2',
})<{ v2?: boolean }>(({ v2 }) => ({
  paddingTop: v2
    ? TAB_CONTENT_PADDING_VERTICAL_V2
    : TAB_CONTENT_PADDING_VERTICAL,
  paddingBottom: v2
    ? TAB_CONTENT_PADDING_VERTICAL_V2
    : TAB_CONTENT_PADDING_VERTICAL,
  TAB_CONTENT_PADDING_VERTICAL,
  fontSize: fontSize.medium,
  lineHeight: lineHeight.title,
  whiteSpace: 'nowrap',
}));

export const FilledTab = styled('div', {
  shouldForwardProp: prop => prop !== 'noBackground',
})<{ noBackground: boolean }>(({ noBackground }) => ({
  paddingTop: TAB_CONTENT_PADDING_VERTICAL,
  paddingBottom: TAB_CONTENT_PADDING_VERTICAL,
  fontSize: fontSize.medium,
  lineHeight: lineHeight.title,
  whiteSpace: 'nowrap',
  background: colors.eggplant10,
  paddingLeft: 20,
  paddingRight: 20,
  borderRadius: '10px 10px 0 0',
  ...(noBackground && {
    background: 'none',
  }),
}));

// https://reacttraining.com/react-router/web/api/Link/to-object
type LinkToObject = {
  pathname?: string;
  search?: string;
  hash?: string;
  state?: any;
};

export const AnchorTab = ({
  href,
  children,
  active,
  onClick,
  v2 = false,
}: {
  href: string;
  children: ReactNode;
  active?: boolean;
  v2?: boolean;
  onClick?: () => void;
}) => (
  <AnchorTabWrapper href={href} active={active} onClick={onClick} v2={v2}>
    <InnerTab v2={v2}>{children}</InnerTab>
  </AnchorTabWrapper>
);

export const ClickableTab = ({
  children,
  active,
  onClick,
  evenlyFillSpace,
  v2 = false,
}: {
  children: ReactNode;
  active: boolean;
  onClick: () => void;
  evenlyFillSpace?: boolean;
  v2?: boolean;
}) => {
  const [hasEnabledAccessibilityMode] = useAccessibilityMode();
  return (
    <TabWrapper
      accessibilityMode={hasEnabledAccessibilityMode}
      active={active}
      onClick={onClick}
      evenlyFillSpace={evenlyFillSpace}
      v2={v2}
    >
      <InnerTab v2={v2}>{children}</InnerTab>
    </TabWrapper>
  );
};

export const LinkTab = ({
  to,
  children,
  active,
  onClick,
  trackingId,
  v2 = false,
}: {
  children: ReactNode;
  active?: boolean | undefined;
  to: string | LinkToObject;
  onClick?: ((event: SyntheticEvent<HTMLAnchorElement>) => void) | undefined;
  trackingId?: string | undefined;
  v2?: boolean | undefined;
}) => {
  const [hasEnabledAccessibilityMode] = useAccessibilityMode();
  const { history } = useRouter();
  const action = useCallback(
    (event: SyntheticEvent<HTMLAnchorElement>) => {
      if (onClick != null) onClick(event);
      history.replace(to);
    },
    [history, onClick, to],
  );

  return (
    <LinkTabWrapper
      accessibilityMode={hasEnabledAccessibilityMode}
      to={to}
      replace
      active={active}
      v2={v2}
      data-tracking-id={trackingId}
      onClick={action}
      onKeyDown={keyDownMap({
        SPACE: [action, { preventDefault: true }],
        ENTER: [action, { preventDefault: true }],
      })}
    >
      <InnerTab v2={v2}>{children}</InnerTab>
    </LinkTabWrapper>
  );
};

export const NavLinkTab = ({
  to,
  children,
  v2 = false,
}: {
  children: ReactNode;
  to: string | LinkToObject;
  v2?: boolean;
}) => (
  <NavLink
    to={to}
    className={css(navLinkTabStyles.default)}
    activeClassName={css(navLinkTabStyles.active)}
  >
    <InnerTab v2={v2}>{children}</InnerTab>
  </NavLink>
);
